import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MoreVert,
  Fullscreen,
  Refresh,
  FilterList,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  ScatterChart,
  Scatter,
} from 'recharts';

interface PowerBIStyleDashboardProps {
  data: any;
  loading: boolean;
  selectedProject: string;
  dateRange: string;
}

interface KPICardProps {
  title: string;
  value: string | number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  color: string;
  icon?: React.ReactNode;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, change, trend, color, icon }) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ color: 'success.main' }} />;
      case 'down':
        return <TrendingDown sx={{ color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'success.main';
      case 'down':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}15, ${color}05)` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getTrendIcon()}
          <Typography variant="body2" sx={{ color: getTrendColor() }}>
            {change > 0 ? '+' : ''}{change}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            vs last period
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const PowerBIStyleDashboard: React.FC<PowerBIStyleDashboardProps> = ({
  data,
  loading,
  selectedProject,
  dateRange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Helpers to compute KPI values from live data
  const formatPercent = (val?: number) => {
    if (val === undefined || val === null || Number.isNaN(val)) return '—';
    const n = typeof val === 'number' ? val : Number(val);
    return `${Math.round(n)}%`;
  };

  const schedule = data?.performanceMetrics?.schedule || data?.performanceMetrics?.projectCompletion;
  const scheduleTotal = (schedule?.ahead || 0) + ((schedule?.onTrack ?? schedule?.onTime) || 0) + (schedule?.delayed || 0);
  const scheduleOnTrack = ((schedule?.onTrack ?? schedule?.onTime) || 0) + (schedule?.ahead || 0);
  const schedulePerf = scheduleTotal > 0 ? Math.round((scheduleOnTrack / scheduleTotal) * 100) : undefined;

  const kpis = [
    {
      title: 'Total Projects',
      value: data?.projectStats?.totalProjects ?? '—',
      change: 0,
      trend: 'neutral' as const,
      color: '#1976d2',
    },
    {
      title: 'Budget Utilization',
      value: formatPercent(data?.projectStats?.budgetStats?.budgetUtilization),
      change: 0,
      trend: 'neutral' as const,
      color: '#f57c00',
    },
    {
      title: 'Schedule Performance',
      value: formatPercent(schedulePerf),
      change: 0,
      trend: 'neutral' as const,
      color: '#388e3c',
    },
    {
      title: 'Average Safety Score',
      value: formatPercent(data?.dailyReportStats?.averageSafetyScore ?? data?.safetyStats?.riskScoreAverage),
      change: 0,
      trend: 'neutral' as const,
      color: '#7b1fa2',
    },
  ];

  // Chart: Schedule distribution from live data
  const performanceChartData = [
    { category: 'Ahead', value: schedule?.ahead || 0 },
    { category: 'On Track', value: (schedule?.onTrack ?? schedule?.onTime) || 0 },
    { category: 'Delayed', value: schedule?.delayed || 0 },
  ];

  // Cost breakdown from live data (fallback to empty)
  const costBreakdown = (data?.costData?.breakdown || []).map((item: any) => ({
    name: item?.label ?? item?.name ?? 'Unknown',
    value: Number(item?.value ?? 0),
    color: item?.color ?? '#1976d2',
  }));

  // Schedule task performance from live data (fallback to empty)
  const scheduleTasks = (data?.scheduleData?.tasks || []).map((t: any) => ({
    task: t?.name ?? t?.task ?? 'Task',
    planned: Number(t?.planned ?? t?.plannedDays ?? t?.plannedDuration ?? 0),
    actual: Number(t?.actual ?? t?.actualDays ?? t?.actualDuration ?? 0),
  }));

  // Risk matrix from live data (fallback to empty)
  const riskMatrix = (data?.riskData || data?.safetyStats?.riskDistribution || []).map((r: any) => ({
    probability: Number(r?.probability ?? r?.prob ?? 0),
    impact: Number(r?.impact ?? 0),
  }));

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Box key={item} sx={{ flex: 1 }}>
              <Skeleton variant="rectangular" height={120} />
            </Box>
          ))}
        </Box>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* KPI Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {kpis.map((kpi, index) => (
          <Box key={index} sx={{ flex: 1 }}>
            <KPICard {...kpi} />
          </Box>
        ))}
      </Box>

      {/* Main Charts */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Schedule Distribution */}
        <Box sx={{ flex: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Schedule Distribution</Typography>
                <Box>
                  <IconButton size="small">
                    <FilterList />
                  </IconButton>
                  <IconButton size="small">
                    <Fullscreen />
                  </IconButton>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#1976d2" name="Projects" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Cost Breakdown */
        }
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Breakdown
              </Typography>
              {costBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={costBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    >
                      {costBreakdown.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No cost breakdown data</Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Schedule Analysis */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schedule Performance by Task
              </Typography>
              {scheduleTasks.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={scheduleTasks} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="task" type="category" width={80} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="planned" fill="#1976d2" name="Planned Days" />
                    <Bar dataKey="actual" fill="#388e3c" name="Actual Days" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No schedule task data</Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Risk Analysis */}
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Analysis Matrix
              </Typography>
              {riskMatrix.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="probability"
                      name="Probability"
                      unit="%"
                      domain={[0, 100]}
                    />
                    <YAxis
                      type="number"
                      dataKey="impact"
                      name="Impact"
                      unit="%"
                      domain={[0, 100]}
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter
                      name="Risks"
                      data={riskMatrix}
                      fill="#f57c00"
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No risk data</Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* AI Insights (from live data if available) */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI-Powered Insights
          </Typography>
          {Array.isArray(data?.aiInsights) && data.aiInsights.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {data.aiInsights.slice(0, 3).map((ins: any, idx: number) => (
                <Paper key={idx} sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {ins.type?.toUpperCase() || 'INSIGHT'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {ins.title || 'Untitled insight'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ins.description || ''}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 2, color: 'text.secondary' }}>No AI insights available</Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PowerBIStyleDashboard;